import React, { createContext, useState, useContext, useEffect } from 'react';
import { buildApiUrl } from '../config/api';
import { authFetch } from '../utils/authFetch';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Always fetch fresh user data from server to get current verification status
          // Use authFetch which automatically handles token refresh on 401
          const response = await authFetch(buildApiUrl('/auth/me/'));

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(data.user));
          } else {
            // Token invalid even after refresh attempt, clear storage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Error loading user:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const register = async (email, password, passwordConfirm) => {
    try {
      const response = await fetch(buildApiUrl('/auth/register/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          password_confirm: passwordConfirm,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store tokens
      localStorage.setItem('access_token', data.tokens.access);
      localStorage.setItem('refresh_token', data.tokens.refresh);

      // Fetch fresh user data from server to get current verification status
      try {
        const userResponse = await fetch(buildApiUrl('/auth/me/'), {
          headers: {
            'Authorization': `Bearer ${data.tokens.access}`,
          },
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData.user);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData.user));
          return { success: true, user: userData.user };
        } else {
          // If we can't fetch fresh data, use the data from register response
          setUser(data.user);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(data.user));
          return { success: true, user: data.user };
        }
      } catch (error) {
        console.error('Error fetching fresh user data:', error);
        // Fallback to register response data
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(buildApiUrl('/auth/login/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store tokens
      localStorage.setItem('access_token', data.tokens.access);
      localStorage.setItem('refresh_token', data.tokens.refresh);

      // Fetch fresh user data from server to get current verification status
      try {
        const userResponse = await fetch(buildApiUrl('/auth/me/'), {
          headers: {
            'Authorization': `Bearer ${data.tokens.access}`,
          },
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData.user);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData.user));
          return { success: true, user: userData.user };
        } else {
          // If we can't fetch fresh data, use the data from login response
          setUser(data.user);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(data.user));
          return { success: true, user: data.user };
        }
      } catch (error) {
        console.error('Error fetching fresh user data:', error);
        // Fallback to login response data
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const accessToken = localStorage.getItem('access_token');

      if (refreshToken && accessToken) {
        // Call logout endpoint to blacklist token
        await fetch(buildApiUrl('/auth/logout/'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            refresh: refreshToken,
          }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call result
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const response = await fetch(buildApiUrl('/auth/token/refresh/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh: refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      
      return data.access;
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const refreshUserInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return;
      }

      // Use authFetch which automatically handles token refresh on 401
      const response = await authFetch(buildApiUrl('/auth/me/'));

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(data.user));
        return data.user;
      } else {
        // If still not ok after refresh attempt, logout
        logout();
      }
    } catch (error) {
      console.error('Error refreshing user info:', error);
      logout();
    }
  };

  const verifyEmailAndRefresh = async () => {
    const result = await refreshUserInfo();
    return result;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    login,
    logout,
    refreshAccessToken,
    getAuthHeaders,
    refreshUserInfo,
    verifyEmailAndRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
